<script setup lang="ts">
import {useExpertStore} from '@/store/expert';
import {computed, onMounted, ref} from 'vue'

defineProps<{
  error?: string
}>()

interface Timezone {
  value: string
  label: string
  offset: string
}

const model = defineModel<string>()
const timezones = ref<Timezone[]>([])
const searchTerm = ref('')
const showDropdown = ref(false)
const expertStore = useExpertStore();

const filteredTimezones = computed(() => {
  if (!searchTerm.value) return timezones.value
  return timezones.value.filter(timezone =>
      timezone.label.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      timezone.value.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
})

const selectTimezone = (timezoneValue: string) => {
  const selectedTimezone = timezones.value.find(tz => tz.value === timezoneValue)
  if (selectedTimezone) {
    model.value = timezoneValue
    searchTerm.value = selectedTimezone.label
    showDropdown.value = false
  }
}

const filterTimezones = () => {
  model.value = searchTerm.value
}

const hideDropdown = () => {
  setTimeout(() => showDropdown.value = false, 100)
}

const generateTimezones = () => {
  const timezones: Timezone[] = []

  const timezoneData = [
    { offset: -12, name: 'Baker Island Time', abbr: 'BIT' },
    { offset: -11, name: 'Niue Time', abbr: 'NUT' },
    { offset: -10, name: 'Hawaii-Aleutian Standard Time', abbr: 'HST' },
    { offset: -9, name: 'Alaska Standard Time', abbr: 'AKST' },
    { offset: -8, name: 'Pacific Standard Time', abbr: 'PST' },
    { offset: -7, name: 'Mountain Standard Time', abbr: 'MST' },
    { offset: -6, name: 'Central Standard Time', abbr: 'CST' },
    { offset: -5, name: 'Eastern Standard Time', abbr: 'EST' },
    { offset: -4, name: 'Atlantic Standard Time', abbr: 'AST' },
    { offset: -3, name: 'Argentina Time', abbr: 'ART' },
    { offset: -2, name: 'South Georgia Time', abbr: 'GST' },
    { offset: -1, name: 'Cape Verde Time', abbr: 'CVT' },
    { offset: 0, name: 'Greenwich Mean Time', abbr: 'GMT' },
    { offset: 1, name: 'Central European Time', abbr: 'CET' },
    { offset: 2, name: 'Eastern European Time', abbr: 'EET' },
    { offset: 3, name: 'Moscow Time', abbr: 'MSK' },
    { offset: 4, name: 'Gulf Standard Time', abbr: 'GST' },
    { offset: 5, name: 'Pakistan Standard Time', abbr: 'PKT' },
    { offset: 5.5, name: 'India Standard Time', abbr: 'IST' },
    { offset: 6, name: 'Bangladesh Standard Time', abbr: 'BST' },
    { offset: 7, name: 'Indochina Time', abbr: 'ICT' },
    { offset: 8, name: 'China Standard Time', abbr: 'CST' },
    { offset: 9, name: 'Japan Standard Time', abbr: 'JST' },
    { offset: 10, name: 'Australian Eastern Standard Time', abbr: 'AEST' },
    { offset: 11, name: 'Solomon Islands Time', abbr: 'SBT' },
    { offset: 12, name: 'New Zealand Standard Time', abbr: 'NZST' },
  ]

  timezoneData.forEach(tz => {
    const sign = tz.offset >= 0 ? '+' : '-'
    const absOffset = Math.abs(tz.offset)
    const hours = Math.floor(absOffset)
    const minutes = (absOffset % 1) * 60
    const offsetString = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`

    timezones.push({
      value: `GMT${offsetString}`,
      label: `(GMT${offsetString}) ${tz.name}`,
      offset: offsetString
    })
  })

  return timezones.sort((a, b) => {
    const aOffset = parseFloat(a.offset.replace(':', '.'))
    const bOffset = parseFloat(b.offset.replace(':', '.'))
    return aOffset - bOffset
  })
}

onMounted(async () => {
  try {
    // Try to fetch from API first, fallback to generated data
    try {
      timezones.value = await expertStore.fetchTimezones()
    } catch (apiError) {
      console.warn('API fetch failed, using generated timezones:', apiError)
      timezones.value = generateTimezones()
    }
  } catch (err) {
    console.error('Failed to load timezones:', err)
    // Fallback to generated timezones
    timezones.value = generateTimezones()
  }
})
</script>

<template>
  <div class="flex flex-col gap-1 relative">
    <label class="block text-h5 font-sm font-archivo text-primary">Timezone</label>
    <input
        v-model="searchTerm"
        @input="filterTimezones"
        @focus="showDropdown = true"
        @blur="hideDropdown"
        placeholder="Select or type timezone"
        class="w-full border px-4 py-2 rounded-md text-primary font-archivo text-paragraph focus:ring-primary focus:border-primary focus:outline-hidden focus:ring-0"
        :class="showDropdown && filteredTimezones.length? 'rounded-bl-none rounded-br-none':''"
    >
    <div v-if="showDropdown && filteredTimezones.length" class="absolute top-[62px] z-10 w-full bg-card border border-primary border-t-0 rounded-md mt-1 max-h-48 overflow-y-auto rounded-tl-none rounded-tr-none">
      <div
          v-for="timezone in filteredTimezones"
          :key="timezone.value"
          @mousedown="selectTimezone(timezone.value)"
          class="px-4 py-2 hover:bg-primary hover:text-card cursor-pointer"
      >
        {{ timezone.label }}
      </div>
    </div>
    <h5 v-if="error" class="text-danger mt-2">
      {{ error }}
    </h5>
  </div>
</template>